import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  sharedFiltersOptions,
  WEBSITE,
  WIDGET_COINS_DEFAULT_CONFIG,
} from 'src/consts'
import { Checkbox } from 'src/components/Checkbox'
import { RadioGroup } from 'src/components/RadioGroup'
import { SelectBox } from 'src/components/Select'
import { Textarea } from 'src/components/Textarea'
import { Widget } from 'src/components/Widget'
import styles from './styles.module.scss'
import { copyToClipboard } from 'src/utils/copyToClipboard'
import { components } from 'react-select'
import { AutoSizer, List } from 'react-virtualized'
import {
  combinedAnalytics,
  convertBooleanToReadableFormat,
} from 'src/utils/common'
import { useSelector } from 'react-redux'
import { getCompleteEntityData } from 'src/redux/selectors'
import clsx from 'clsx'
import { intl } from 'src/i18n'

const settingsDict = {
  secondaryCurrency: ['BTC', 'USD', 'None'],
  primaryCurrency: sharedFiltersOptions.currency,
}

const MenuList = props => {
  const rowRenderer = ({ key, index, style }) => {
    return (
      <div
        key={key}
        className={styles.row}
        style={style}
        onClick={() =>
          props.setValue(props.children[index].props.data, 'set-value')
        }
      >
        <div className={styles.left}>
          {props.children[index].props.data.label}
        </div>
      </div>
    )
  }

  return (
    <components.MenuList {...props}>
      <AutoSizer>
        {({ width }) => (
          <List
            width={width}
            height={props.children.length ? 250 : 0}
            rowCount={props.children.length || 0}
            rowHeight={30}
            rowRenderer={rowRenderer}
          />
        )}
      </AutoSizer>
    </components.MenuList>
  )
}

export const WidgetsCustomizer = ({ symbols, setCoinSlug }) => {
  const successMap = useCopySuccess()
  const successCoin = useCopySuccess()
  const successTop10 = useCopySuccess()
  const coinData = useSelector(getCompleteEntityData)
  const [config, setConfig] = useState(WIDGET_COINS_DEFAULT_CONFIG)

  const toggleSettingHandler = (name, value) => {
    setConfig(d => ({
      ...d,
      [name]: !value,
    }))
  }

  const setPrimaryCurrency = value => {
    setConfig(d => ({
      ...d,
      primaryCurrency: value,
    }))
  }

  const {
    height,
    primaryTickerSymbol,
    primaryCurrency,
    secondaryCurrency,
    showRank,
    showMarketCap,
    showVolume,
    brightVersion,
  } = config

  const coinCode = useMemo(() => {
    const urlParams = Object.entries({
      coin: coinData?.slug,
      ...config,
      utm_source: 'embed_widget_coin',
    }).reduce((acc, [key, value], i) => {
      if (key !== 'coin' && value === WIDGET_COINS_DEFAULT_CONFIG[key])
        return acc
      return `${acc}${i === 0 ? '' : '&'}${key}=${value}`
    }, '')

    return `<iframe src="${WEBSITE}/coin-widget?${urlParams}" width="100%" height="${height}" frameborder="0"/>`
  }, [config, height, coinData?.slug])

  const treeMapCode = `<iframe src="${WEBSITE}/widget/map?utm_source=embed_map" frameborder="0" title="Coin360.com: Cryptocurrency Market State" width="400" height="300"></iframe>`

  const top10MapCode = `<iframe src="${WEBSITE}/widget/top-10?utm_source=embed_map" frameborder="0" title="Coin360.com: Top 10 Coins" width="400" height="300"></iframe>`

  const copyText = async type => {
    if (type === 'map') {
      successMap.show()
      await copyToClipboard(treeMapCode)
      combinedAnalytics('Widget', 'Widget', 'Copy Market State', '')
    } else if (type === 'coin') {
      successCoin.show()
      await copyToClipboard(coinCode)
    } else {
      successTop10.show()
      await copyToClipboard(top10MapCode)
      const {
        primaryCurrency,
        primaryTickerSymbol,
        secondaryCurrency,
        showRank,
        showMarketCap,
        showVolume,
        brightVersion,
        symbol,
      } = props
      combinedAnalytics(
        'Widget',
        'Widget',
        `Copy ${symbol}`,
        `Applied options[` +
          `Primary Display Currency ${primaryCurrency}_` +
          `Primary Ticker Symbol ${convertBooleanToReadableFormat(
            primaryTickerSymbol
          )}_` +
          `Secondary display currency ${
            secondaryCurrency === 'None' ? 'Off' : secondaryCurrency
          }_` +
          `Rank ${convertBooleanToReadableFormat(showRank)}_` +
          `Market Cap ${convertBooleanToReadableFormat(showMarketCap)}_` +
          `Volume ${convertBooleanToReadableFormat(showVolume)}_` +
          `Bright Version ${convertBooleanToReadableFormat(brightVersion)}_` +
          `Select Coin ${symbol}` +
          `]`
      )
    }
  }

  return (
    <>
      <h2 className={styles.title}>
        {intl.t('coinDetails.widgetsCustomizer.title')}
      </h2>
      <div className={styles.wrapper}>
        <div className={styles.settings}>
          {symbols && (
            <>
              <div className={styles.settingsLabel}>
                {intl.t('coinDetails.widgetsCustomizer.select')}
              </div>
              <SelectBox
                className={clsx(styles.select, styles.selectCoins)}
                options={symbols}
                value={coinData?.slug}
                action={value => setCoinSlug(value)}
                components={{
                  MenuList: menuProps => <MenuList {...menuProps} />,
                }}
              />
            </>
          )}

          <div className={styles.settingsLabel}>
            {intl.t('coinDetails.widgetsCustomizer.primaryDisplay')}
          </div>
          <SelectBox
            className={styles.select}
            options={sharedFiltersOptions.currency}
            value={primaryCurrency}
            action={value => setPrimaryCurrency(value)}
          />
          <RadioGroup
            className={styles.radioGroup}
            currentValue={secondaryCurrency}
            label={intl.t('coinDetails.widgetsCustomizer.label1')}
            name='secondaryCurrency'
            onClick={({ name, value }) =>
              setConfig(d => ({ ...d, [name]: value }))
            }
            options={settingsDict.secondaryCurrency}
          />
          <Checkbox
            className={styles.checkbox}
            label={intl.t('coinDetails.widgetsCustomizer.label2')}
            onChange={() =>
              toggleSettingHandler('primaryTickerSymbol', primaryTickerSymbol)
            }
            checked={primaryTickerSymbol}
          />
          <Checkbox
            className={styles.checkbox}
            label={intl.t('coinDetails.widgetsCustomizer.label3')}
            onChange={() => toggleSettingHandler('showRank', showRank)}
            checked={showRank}
          />
          <Checkbox
            className={styles.checkbox}
            label={intl.t('coinDetails.widgetsCustomizer.label4')}
            onChange={() =>
              toggleSettingHandler('showMarketCap', showMarketCap)
            }
            checked={showMarketCap}
          />
          <Checkbox
            className={styles.checkbox}
            label={intl.t('coinDetails.widgetsCustomizer.label5')}
            onChange={() => toggleSettingHandler('showVolume', showVolume)}
            checked={showVolume}
          />
          <div className={styles.settingsLabel}>
            {intl.t('coinDetails.widgetsCustomizer.colors')}
          </div>
          <Checkbox
            className={styles.checkbox}
            label={intl.t('coinDetails.widgetsCustomizer.label6')}
            onChange={() =>
              toggleSettingHandler('brightVersion', brightVersion)
            }
            checked={brightVersion}
          />
        </div>
        <div className={styles.container}>
          <div className={styles.widget}>
            <div dangerouslySetInnerHTML={{ __html: coinCode }} />
          </div>
          <Textarea text={coinCode} />
          <div className={styles.copyButtonWrapper}>
            <button
              className={styles.copyButton}
              onClick={() => copyText('coin')}
            >
              {intl.t('coinDetails.widgetsCustomizer.buttonCopy')}
            </button>
            {successCoin.isShowing && (
              <div className={styles.successfulCopy}>
                {intl.t('coinDetails.widgetsCustomizer.copied')}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.separateLine} />
      <h2 className={styles.title}>
        {intl.t('coinDetails.widgetsCustomizer.marketState')}
      </h2>
      <div className={styles.wrapper}>
        <div className={styles.mapWidgetInnerContainer}>
          <section className={styles.mapWidget}>
            <div dangerouslySetInnerHTML={{ __html: treeMapCode }} />
          </section>
        </div>
        <div className={styles.widgetContainer}>
          <Textarea text={treeMapCode} />
          <div className={styles.copyButtonWrapper}>
            <button
              className={styles.copyButton}
              onClick={() => copyText('map')}
            >
              {intl.t('coinDetails.widgetsCustomizer.buttonCopy')}
            </button>
            {successMap.isShowing && (
              <div className={styles.successfulCopy}>
                {intl.t('coinDetails.widgetsCustomizer.copied')}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.separateLine} />
      <h2 className={styles.title}>
        {intl.t('coinDetails.widgetsCustomizer.top10')}
      </h2>
      <div className={styles.wrapper}>
        <div className={styles.mapWidgetInnerContainer}>
          <section className={styles.mapWidget}>
            <div dangerouslySetInnerHTML={{ __html: top10MapCode }} />
          </section>
        </div>
        <div className={styles.widgetContainer}>
          <Textarea text={top10MapCode} />
          <div className={styles.copyButtonWrapper}>
            <button
              className={styles.copyButton}
              onClick={() => copyText('top10')}
            >
              {intl.t('coinDetails.widgetsCustomizer.buttonCopy')}
            </button>
            {successTop10.isShowing && (
              <div className={styles.successfulCopy}>
                {intl.t('coinDetails.widgetsCustomizer.copied')}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={styles.separateLine} />
    </>
  )
}

function useCopySuccess() {
  const [isShowing, setIsShowing] = useState(false)
  useEffect(() => {
    let timeout
    if (isShowing) {
      timeout = setTimeout(() => setIsShowing(false), 2000)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
    }
  }, [isShowing])

  const show = useCallback(() => setIsShowing(true), [])

  return { isShowing, show }
}

export default WidgetsCustomizer
